/**
 * Le réglage de la saisie des clients, vu sur TOUS les dossiers.
 *
 * ⚠️ CE N'EST PAS UN ÉCRAN, C'EST UN PANNEAU. Décision de Rose du 16/08/2026,
 * après que j'aie eu le tort d'en faire une entrée de menu : LA LISTE DES
 * DOSSIERS EST LE POINT D'ENTRÉE, et les paramétrages sont derrière UN SEUL
 * bouton « Paramètres ». Un menu par tâche, ou même un bouton par tâche, et la
 * barre d'outils devient à son tour le problème.
 *
 * DEUX QUESTIONS, DEUX RÉPONSES :
 *
 * 1. PAR QUEL DOSSIER JE CONTINUE. Le réglage se fait dossier par dossier, ce
 *    qui est juste mais aveugle — au bout de trente, personne ne sait lesquels
 *    restent. D'où les deux vues « à régler » / « réglés » et le compte.
 *
 * 2. COMMENT NE PAS LE FAIRE TRENTE FOIS. Une prime de panier repas au même
 *    tarif se retrouve sur trente dossiers du même secteur. On coche, on décrit
 *    la colonne une fois, elle est posée partout.
 *
 * PAS DE CARTES D'INDICATEURS : une phrase dit le compte, la liste commence
 * tout en haut.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Plus, Search, X } from 'lucide-react';
import { api } from '../services/api';

interface Couverture {
  folderUuid: string;
  nom: string;
  domaine: string | null;
  actives: number;
  libelles: string[];
}

type Vue = 'a_regler' | 'regles';

const UNITES = [
  { cle: 'montant', libelle: 'Un montant en €' },
  { cle: 'nombre', libelle: 'Un nombre' },
  { cle: 'heures', libelle: 'Des heures' },
] as const;

export function PanneauCouvertureSaisie({
  ouvert,
  onFermer,
}: {
  ouvert: boolean;
  onFermer: () => void;
}) {
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState<Couverture[]>([]);
  const [charge, setCharge] = useState(false);
  const [vue, setVue] = useState<Vue>('a_regler');
  const [recherche, setRecherche] = useState('');
  const [choisis, setChoisis] = useState<Set<string>>(new Set());
  const [formulaireOuvert, setFormulaireOuvert] = useState(false);
  const [resultat, setResultat] = useState<string | null>(null);

  const recharger = () =>
    api
      .get('/saisie-client/colonnes/couverture')
      .then(r => setDossiers(r.data.data))
      .catch(() => setDossiers([]))
      .finally(() => setCharge(true));

  useEffect(() => {
    if (!ouvert) return;
    // Chargé à l'ouverture, pas au chargement de la liste des dossiers : ce
    // panneau se consulte rarement, il n'a pas à peser sur chaque visite.
    void recharger();
  }, [ouvert]);

  useEffect(() => {
    if (!ouvert) return;
    const surEchap = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (formulaireOuvert) setFormulaireOuvert(false);
      else onFermer();
    };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [ouvert, formulaireOuvert, onFermer]);

  const aRegler = dossiers.filter(d => d.actives === 0);
  const regles = dossiers.filter(d => d.actives > 0);

  const affiches = useMemo(() => {
    const source = vue === 'a_regler' ? aRegler : regles;
    const q = recherche.trim().toLowerCase();
    if (!q) return source;
    return source.filter(
      d =>
        d.nom.toLowerCase().includes(q) ||
        (d.domaine ?? '').toLowerCase().includes(q)
    );
  }, [vue, recherche, dossiers]);

  if (!ouvert) return null;

  const basculer = (uuid: string) =>
    setChoisis(s => {
      const n = new Set(s);
      if (n.has(uuid)) n.delete(uuid);
      else n.add(uuid);
      return n;
    });

  const toutChoisir = () =>
    setChoisis(s =>
      s.size === affiches.length
        ? new Set()
        : new Set(affiches.map(d => d.folderUuid))
    );

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Fermer"
        onClick={onFermer}
        className="flex-1 bg-neutral-900/20"
      />
      <div className="flex h-full w-full max-w-[640px] flex-col bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-[var(--color-primary-100)] px-5 py-3.5">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-neutral-900">
              Ce que les clients peuvent déclarer
            </h2>
            <p className="mt-1 text-[12px] text-neutral-500">
              Les primes et heures propres à chaque dossier
            </p>
          </div>
          <button
            type="button"
            onClick={onFermer}
            aria-label="Fermer"
            className="rounded-lg p-1 text-neutral-400 hover:bg-[var(--color-neutral-50)] hover:text-neutral-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="mb-3 text-[13px] text-neutral-600">
            {charge ? (
              <>
                <span className="font-medium text-neutral-900">
                  {regles.length}
                </span>{' '}
                dossier{regles.length > 1 ? 's' : ''} réglé
                {regles.length > 1 ? 's' : ''} sur {dossiers.length}.{' '}
                {aRegler.length > 0 && (
                  <>
                    Les autres n’ont aucune prime configurée : leurs clients ne
                    voient que les heures supplémentaires et le commentaire.
                  </>
                )}
              </>
            ) : (
              'Chargement…'
            )}
          </p>

          <div className="mb-2 flex flex-wrap items-center gap-2">
            <div className="flex gap-1 rounded-lg border border-[var(--color-primary-100)] bg-white p-1">
              {(
                [
                  {
                    cle: 'a_regler' as const,
                    libelle: 'À régler',
                    n: aRegler.length,
                  },
                  {
                    cle: 'regles' as const,
                    libelle: 'Réglés',
                    n: regles.length,
                  },
                ] satisfies { cle: Vue; libelle: string; n: number }[]
              ).map(o => (
                <button
                  key={o.cle}
                  type="button"
                  onClick={() => {
                    setVue(o.cle);
                    setChoisis(new Set());
                  }}
                  className={`rounded-md px-3 py-1 text-[12.5px] transition-colors ${
                    vue === o.cle
                      ? 'bg-[var(--color-primary-100)] text-[var(--color-primary-600)]'
                      : 'text-neutral-600 hover:text-neutral-900'
                  }`}
                >
                  {o.libelle}
                  <span className="ml-2 text-[11.5px] text-neutral-400">
                    {o.n}
                  </span>
                </button>
              ))}
            </div>

            <div className="relative min-w-0 flex-1 sm:max-w-[220px]">
              <Search className="pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />
              <input
                value={recherche}
                onChange={e => setRecherche(e.target.value)}
                placeholder="Chercher un dossier"
                aria-label="Chercher un dossier"
                className="w-full rounded-lg border border-[var(--color-primary-100)] bg-white py-2 pr-3 pl-8 text-[12.5px] placeholder:text-neutral-400/60"
              />
            </div>

            {affiches.length > 0 && (
              <button
                type="button"
                onClick={toutChoisir}
                className="text-[12px] text-[var(--color-primary-600)] hover:underline"
              >
                {choisis.size === affiches.length
                  ? 'Tout décocher'
                  : `Cocher les ${affiches.length}`}
              </button>
            )}
          </div>

          {resultat && (
            <p className="mb-2 rounded-lg bg-[var(--color-primary-100)] px-3 py-2 text-[12.5px] text-[var(--color-primary-600)]">
              {resultat}
            </p>
          )}

          {charge && affiches.length === 0 && (
            <p className="rounded-xl border border-dashed border-[var(--color-primary-100)] px-4 py-6 text-center text-[13px] text-neutral-500">
              {recherche.trim()
                ? 'Aucun dossier ne correspond.'
                : vue === 'a_regler'
                  ? 'Tous les dossiers sont réglés.'
                  : 'Aucun dossier n’est encore réglé.'}
            </p>
          )}

          <div className="flex flex-col gap-1">
            {affiches.map(d => (
              <div
                key={d.folderUuid}
                className="flex items-center gap-2 rounded-xl border border-[var(--color-primary-100)] bg-white px-3 py-2 transition-colors hover:border-[var(--color-primary-400)]"
              >
                <input
                  type="checkbox"
                  checked={choisis.has(d.folderUuid)}
                  onChange={() => basculer(d.folderUuid)}
                  aria-label={`Choisir ${d.nom}`}
                  className="h-3.5 w-3.5 shrink-0 accent-[var(--color-primary-600)]"
                />
                {/* Le nom mène au dossier, tiroir de réglage ouvert : on ne
                    demande pas de retrouver le menu « ⋯ » une fois arrivé. */}
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/folders/${d.folderUuid}?colonnes=1`)
                  }
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] text-neutral-900">
                      {d.nom}
                    </span>
                    {d.domaine && (
                      <span className="block truncate text-[11.5px] text-neutral-400">
                        {d.domaine}
                      </span>
                    )}
                  </span>
                  {/* Ce que le client voit, en toutes lettres : « 3 colonnes »
                      ne dit pas si ce sont les bonnes. */}
                  <span className="hidden max-w-[50%] min-w-0 truncate text-right text-[12px] text-neutral-500 sm:block">
                    {d.libelles.join(' · ')}
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* La barre d'action groupée n'existe QUE quand quelque chose est
            coché : c'est ce qui empêche cette barre d'être là tous les jours
            pour rien. */}
        {choisis.size > 0 && (
          <div className="border-t border-[var(--color-primary-100)] bg-[var(--color-neutral-50)] px-5 py-3">
            {formulaireOuvert ? (
              <FormulaireGroupe
                nombre={choisis.size}
                onAnnuler={() => setFormulaireOuvert(false)}
                onAppliquer={async c => {
                  const r = await api.post('/saisie-client/colonnes/groupe', {
                    folderUuids: [...choisis],
                    ...c,
                  });
                  const { posees, miseAJour } = r.data.data;
                  setResultat(
                    `« ${c.libelle} » posée sur ${posees} dossier${posees > 1 ? 's' : ''}` +
                      (miseAJour > 0
                        ? `, mise à jour sur ${miseAJour} qui l’avaient déjà.`
                        : '.')
                  );
                  setFormulaireOuvert(false);
                  setChoisis(new Set());
                  await recharger();
                }}
              />
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[12.5px] text-neutral-700">
                  {choisis.size} dossier{choisis.size > 1 ? 's' : ''} coché
                  {choisis.size > 1 ? 's' : ''}
                </span>
                <button
                  type="button"
                  onClick={() => setFormulaireOuvert(true)}
                  className="ml-auto flex items-center gap-2 rounded-lg bg-[var(--color-primary-600)] px-3 py-2 text-[12.5px] font-medium text-white transition-opacity hover:opacity-90"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Poser la même colonne partout
                </button>
                <button
                  type="button"
                  onClick={() => setChoisis(new Set())}
                  className="rounded-lg border border-[var(--color-primary-100)] bg-white px-3 py-2 text-[12.5px] text-neutral-600 hover:border-[var(--color-primary-400)]"
                >
                  Décocher
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * La colonne à poser sur tous les dossiers cochés.
 *
 * Les mêmes champs qu'à l'unité, et la même exigence : le code de rubrique est
 * obligatoire. Une colonne sans rubrique posée sur trente dossiers ferait
 * trente qualifications à faire à la main.
 */
function FormulaireGroupe({
  nombre,
  onAppliquer,
  onAnnuler,
}: {
  nombre: number;
  onAppliquer: (c: {
    code: string;
    libelle: string;
    unite: string;
    valeurUnitaire: number | null;
  }) => Promise<void>;
  onAnnuler: () => void;
}) {
  const [libelle, setLibelle] = useState('');
  const [code, setCode] = useState('');
  const [unite, setUnite] = useState<string>('montant');
  const [valeur, setValeur] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [encours, setEncours] = useState(false);

  const valider = async () => {
    if (libelle.trim().length < 2) {
      setErreur('Donnez le nom que verront les clients.');
      return;
    }
    if (!code.trim()) {
      setErreur('Le code de rubrique OpenPaye est obligatoire.');
      return;
    }
    setErreur(null);
    setEncours(true);
    try {
      await onAppliquer({
        libelle: libelle.trim(),
        code: code.trim().toUpperCase(),
        unite,
        valeurUnitaire: valeur === '' ? null : Number(valeur),
      });
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'L’enregistrement a échoué.');
    } finally {
      setEncours(false);
    }
  };

  return (
    <div>
      <p className="mb-2 text-[12.5px] text-neutral-700">
        La même colonne sur {nombre} dossier{nombre > 1 ? 's' : ''}
        {/* Dit AVANT, pas après : un dossier qui a déjà cette rubrique voit
            son libellé et son tarif remplacés (verdict QA du 22/09, M6). */}
        <span className="text-neutral-500">
          {' '}
          — un dossier qui a déjà cette rubrique sera mis à jour
        </span>
      </p>
      <div className="mb-2 grid grid-cols-4 gap-2">
        <input
          value={libelle}
          onChange={e => setLibelle(e.target.value)}
          placeholder="Nom vu par le client"
          aria-label="Nom vu par le client"
          className="col-span-2 rounded-lg border border-[var(--color-primary-100)] bg-white px-3 py-2 text-[13px] placeholder:text-neutral-400/50"
        />
        <input
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder="Rubrique"
          aria-label="Rubrique OpenPaye"
          className="rounded-lg border border-[var(--color-primary-100)] bg-white px-3 py-2 text-[13px] uppercase placeholder:text-neutral-400/50"
        />
        <input
          type="number"
          step="0.01"
          value={valeur}
          onChange={e => setValeur(e.target.value)}
          placeholder="8,50"
          aria-label="Valeur unitaire"
          className="rounded-lg border border-[var(--color-primary-100)] bg-white px-3 py-2 text-right text-[13px] tabular-nums placeholder:text-neutral-400/50"
        />
      </div>
      <div className="mb-2 flex flex-wrap gap-2">
        {UNITES.map(u => (
          <button
            key={u.cle}
            type="button"
            onClick={() => setUnite(u.cle)}
            className={`rounded-full border px-3 py-1 text-[12px] transition-colors ${
              unite === u.cle
                ? 'border-[var(--color-primary-600)] bg-[var(--color-primary-100)] text-[var(--color-primary-600)]'
                : 'border-[var(--color-primary-100)] bg-white text-neutral-700 hover:border-[var(--color-primary-400)]'
            }`}
          >
            {u.libelle}
          </button>
        ))}
      </div>

      {erreur && (
        <p className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-[12.5px] text-red-800">
          {erreur}
        </p>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          disabled={encours}
          onClick={() => void valider()}
          className="flex items-center gap-2 rounded-lg bg-[var(--color-primary-600)] px-3 py-2 text-[12.5px] font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          <Check className="h-3.5 w-3.5" />
          {encours ? 'Application…' : `Appliquer aux ${nombre}`}
        </button>
        <button
          type="button"
          onClick={onAnnuler}
          className="rounded-lg border border-[var(--color-primary-100)] bg-white px-3 py-2 text-[12.5px] text-neutral-600 hover:border-[var(--color-primary-400)]"
        >
          Annuler
        </button>
      </div>
    </div>
  );
}
